/**
 * Modal that captures a courier UUID and assigns it to a given order.
 *
 * Lives under `components/domain` so any feature that lists orders (the
 * global `/admin/orders` detail page or the inline merchant-ops orders
 * tab) can reuse the same dialog without re-implementing the validation
 * + mutation wiring. The courier picker (with search / availability
 * filtering) belongs to the courier ops feature; here we only push a
 * known UUID down the wire.
 *
 * Uses RHF + Zod so the assign button stays disabled until the input
 * parses as a UUID. Toast feedback is handled in-component to keep
 * consumers as a 1-line render.
 *
 * Two API shapes are supported:
 *
 * - **Trigger-based** (default): pass a `trigger` element; the dialog
 *   manages its own open state and clicks on the trigger toggle it.
 * - **Controlled**: pass `open` + `onOpenChange` (and omit `trigger`);
 *   the consumer drives the dialog from elsewhere — typical when the
 *   open command comes from a Radix `<DropdownMenu>` item, where
 *   nesting another portal would fight the menu's focus trap.
 */
import { useState, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@favelafood/shared/components/ui';
import { isApiError } from '@favelafood/shared/api/errors';
import { toast } from '@favelafood/shared/lib/toast';
import { useAssignCourier } from '@/features/orders/hooks';

const schema = z.object({
  courierProfileId: z
    .string()
    .uuid('Informe um UUID válido do entregador'),
});

type FormValues = z.infer<typeof schema>;

export interface AssignCourierDialogProps {
  orderId: string;
  /** Trigger element to render. Required in uncontrolled mode. */
  trigger?: ReactElement;
  /** Controlled open flag. When provided, the consumer owns visibility. */
  open?: boolean;
  /** Controlled open setter. Required when `open` is provided. */
  onOpenChange?: (next: boolean) => void;
  /** Optional callback invoked after a successful assignment. */
  onAssigned?: () => void;
}

export function AssignCourierDialog({
  orderId,
  trigger,
  open: openProp,
  onOpenChange,
  onAssigned,
}: AssignCourierDialogProps): ReactElement {
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (next: boolean): void => {
    if (isControlled) {
      onOpenChange?.(next);
    } else {
      setInternalOpen(next);
    }
  };

  const assign = useAssignCourier();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { courierProfileId: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await assign.mutateAsync({ id: orderId, courierProfileId: values.courierProfileId });
      toast.success('Entregador atribuído');
      reset();
      setOpen(false);
      onAssigned?.();
    } catch (err: unknown) {
      if (isApiError(err)) {
        toast.fromApiError(err);
        return;
      }
      toast.error('Falha ao atribuir entregador');
    }
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger ? (
        <span
          // The trigger is rendered inline; we wrap in a span so we can
          // attach the open handler without forcing the consumer to
          // forward refs through Radix asChild.
          onClick={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setOpen(true);
            }
          }}
          role="button"
          tabIndex={0}
        >
          {trigger}
        </span>
      ) : null}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Atribuir entregador</DialogTitle>
          <DialogDescription>
            Cole o ID do perfil do entregador. A criação da entrega será feita
            imediatamente.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate className="mt-4 flex flex-col gap-4">
          <Input
            label="ID do perfil do entregador"
            placeholder="00000000-0000-0000-0000-000000000000"
            {...(errors.courierProfileId?.message
              ? { error: errors.courierProfileId.message }
              : {})}
            {...register('courierProfileId')}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setOpen(false)}
              disabled={assign.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={assign.isPending}>
              Atribuir
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
